import {
  AnyGalleryEntityVersion,
  Collections,
  FirestoreEntity,
  Gallery,
  GalleryEntity,
  isNonEmptyString,
  Mapper,
} from '@uploadcam/common';
import { CollectionReference, Firestore, Timestamp, addDoc } from 'firebase/firestore';
import { getCollectionRef } from '../../../../@harmony/angular';
import invariant from 'tiny-invariant';

export interface CreateGalleryData {
  title: string;
  description?: string;
  /**
   * Plaintext password and its hash. Either both fields are provided together or both
   * are omitted — the password is also stored in cleartext so anyone with read access
   * can reveal it to share with public viewers.
   */
  publicPassword?: string;
  publicPasswordHash?: string;
  /** Extra users (beyond the owner) who can contribute. Ignored if `isOpenToTeam` is true. */
  contributorUserIds?: string[];
  /** When true, any team member can contribute. */
  isOpenToTeam?: boolean;
  /** When the gallery should stop being publicly viewable. Omit for "never expires". */
  expiresAt?: Date;
}

export class CreateGalleryInteractor {
  private readonly collection: CollectionReference<AnyGalleryEntityVersion>;

  constructor(
    private readonly db: Firestore,
    private readonly mapper: Mapper<FirestoreEntity<AnyGalleryEntityVersion>, Gallery>,
  ) {
    this.collection = getCollectionRef(this.db, Collections.Galleries);
  }

  public async execute(
    teamId: string,
    createdBy: string,
    data: CreateGalleryData,
  ): Promise<Gallery> {
    invariant(isNonEmptyString(teamId), `'teamId' must be defined`);
    invariant(isNonEmptyString(createdBy), `'createdBy' must be defined`);
    invariant(isNonEmptyString(data.title), `'title' must be defined`);

    const now = Timestamp.now();
    const slug = generateSlug();

    const entity: GalleryEntity = {
      _version: 0,
      slug,
      title: data.title.trim(),
      description: data.description?.trim() || undefined,
      teamId,
      createdBy,
      createdAt: now,
      updatedAt: now,
      itemIds: [],
      publicPasswordHash: data.publicPasswordHash,
      publicPassword: data.publicPassword,
      contributorUserIds:
        data.contributorUserIds && data.contributorUserIds.length > 0
          ? data.contributorUserIds
          : undefined,
      isOpenToTeam: data.isOpenToTeam || undefined,
      expiresAt: data.expiresAt ? Timestamp.fromDate(data.expiresAt) : undefined,
    };

    const docRef = await addDoc(this.collection, entity);

    return this.mapper.map(new FirestoreEntity(docRef.id, entity));
  }
}

/**
 * 32-char hex slug from crypto.randomUUID() — ~122 bits of entropy, URL-safe.
 * Long enough that direct guessing is infeasible; the slug is the share secret.
 */
function generateSlug(): string {
  return crypto.randomUUID().replace(/-/g, '');
}
